import { useEffect, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { Figure, Flow } from '../components/Diagram';

const STEPS: { title: string; body: string; entity?: string; entityLabel?: string; figure?: ReactNode }[] = [
  {
    title: 'Who can invite',
    body: 'Only an Admin or Owner of the workspace can add people. Members can sign in and work, but they can\'t manage the team. If you don\'t see the invite control, ask an admin on your account to grant you the role or send the invite for you.',
    entity: 'tenant',
    entityLabel: 'Tenant',
  },
  {
    title: 'Open the team list',
    body: 'Click Users in the sidebar (also reachable at /users). This is the roster for your workspace — everyone with access, their role, and when they last signed in. It is scoped to your workspace only; inviting someone here never touches any other tenant.',
  },
  {
    title: 'Invite by email',
    body: 'Click Invite Team Member, enter the person\'s email address and (optionally) their name, and choose a role — Admin (can manage the catalog, billing, and the team) or Member (day-to-day access without team management). Owners have the same reach as admins plus ownership of the workspace. Submit to send the invite.',
  },
  {
    title: 'What happens next',
    body: 'Revvly creates a sign-in account for that email and emails them a secure link to set their own password. Their membership is active immediately — once they set a password and sign in, they land in your workspace with the role you chose. You never set or see their password. If the email already has a Revvly account, they\'re simply added to your workspace and no setup email is sent.',
    figure: (
      <Figure
        title="After you send the invite"
        caption="A new email gets a setup link; an email that already has an account is simply added."
      >
        <Flow
          steps={[
            { title: 'You submit the invite', text: 'Email, optional name and role.' },
            {
              fork: [
                {
                  title: 'New email',
                  text: 'An account is created and a secure link to set a password is emailed.',
                  tone: 'accent',
                },
                {
                  title: 'Existing Revvly account',
                  text: 'Added to your workspace. No setup email is sent.',
                  tone: 'muted',
                },
              ],
            },
            { title: 'Active immediately', text: 'After signing in they land in your workspace with the chosen role.' },
          ]}
        />
      </Figure>
    ),
  },
  {
    title: 'Change a role or revoke access',
    body: 'Back on Users, use the row actions to open Change Role or to Deactivate someone, which revokes their access. Changes take effect on their next request. Deactivating someone only unlinks them from this workspace — it doesn\'t delete their account or any data.',
  },
];

export function AddUserGuide() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Add a team member — Revvly Docs';
  }, []);

  return (
    <div className="tn-page">
      <nav className="dp-crumbs" aria-label="Breadcrumb">
        <Link to="/docs/guides">Guides</Link>
        <span aria-hidden="true">›</span>
        <span className="dp-crumb-current">Add a team member</span>
      </nav>

      <header className="dp-header">
        <span className="rv-eyebrow-mini">Guide</span>
        <h1>
          Add a <em>team member</em>.
        </h1>
        <p className="dp-oneliner">
          Invite a colleague into your workspace by email, pick their role, and let them set their
          own password — active immediately, scoped to your workspace alone.
        </p>
      </header>

      {STEPS.map((step, i) => (
        <div key={i} className="dp-section">
          <h2>
            <span style={{ color: 'var(--rv-fg-dark-5)', fontWeight: 400, marginRight: 12 }}>
              {String(i + 1).padStart(2, '0')}
            </span>
            {step.title}
          </h2>
          <p className="dp-plain">{step.body}</p>
          {step.figure}
          {step.entity && (
            <ul className="dp-connects" style={{ marginTop: 14 }}>
              <li>
                <Link to={`/docs/concepts/${step.entity}`} style={{ color: 'inherit', textDecoration: 'none' }}>
                  {step.entityLabel} concept ›
                </Link>
              </li>
            </ul>
          )}
        </div>
      ))}
    </div>
  );
}
